'use client';

import { getColorScheme } from '@/utils/getColorScheme';
import { buttonLinkTargetProps } from '@/utils/buttonLinkTarget';

const ACTIONS = {
    text: {
        icon: 'fa-solid fa-message-sms',
        label: 'Text Now',
    },
    book: {
        icon: 'fa-solid fa-calendar-check',
        label: 'Book Now',
    },
    call: {
        icon: 'fa-solid fa-phone',
        label: 'Call Now',
    },
};

function coerceBoolean(value, fallback = true) {
    if (typeof value === 'boolean') return value;
    if (typeof value === 'string') {
        const normalized = value.trim().toLowerCase();
        if (['false', '0', 'no', 'off'].includes(normalized)) return false;
        if (['true', '1', 'yes', 'on'].includes(normalized)) return true;
    }
    return fallback;
}

function normalizeAction(value) {
    const normalized = String(value || 'text')
        .trim()
        .toLowerCase();
    return ACTIONS[normalized] ? normalized : 'text';
}

function normalizePhone(value) {
    const raw = String(value || '').trim();
    if (!raw) return '';
    const prefix = raw.startsWith('+') ? '+' : '';
    return `${prefix}${raw.replace(/\D/g, '')}`;
}

function normalizeBookingLink(value) {
    const raw = String(value || '').trim();
    if (!raw) return '#';
    if (/^(https?:|mailto:|tel:|sms:|\/|#)/i.test(raw)) return raw;
    return `https://${raw}`;
}

function getActionHref(action, phoneNumber, bookingLink, textMessage) {
    if (action === 'book') return normalizeBookingLink(bookingLink);

    const phone = normalizePhone(phoneNumber);
    if (!phone) return '#';
    if (action === 'call') return `tel:${phone}`;

    const message = String(textMessage || '').trim();
    return `sms:${phone}${
        message ? `?body=${encodeURIComponent(message)}` : ''
    }`;
}

export default function FloatingAction(props) {
    const {
        Enabled = true,
        Type,
        Action,
        Label,
        PhoneNumber = '',
        BookingLink = '',
        BookingLinkOpenInNewTab = false,
        TextMessage = '',
        Icon = '',
        ShowLabel = true,
        Position = 'right',
        ColorScheme = 'primary',
        GradientDirection = 180,
        is_edit_mode = false,
    } = props;

    const enabled = coerceBoolean(Enabled, true);
    if (!enabled) return null;

    const action = normalizeAction(Type || Action);
    const actionConfig = ACTIONS[action];
    const label = String(Label || '').trim() || actionConfig.label;
    const icon = String(Icon || '').trim() || actionConfig.icon;
    const showLabel = coerceBoolean(ShowLabel, true);
    const isLeft = String(Position || '').toLowerCase() === 'left';
    const hasBookingLink = Boolean(String(BookingLink || '').trim());
    const useNiceJobLead = action === 'book' && !hasBookingLink;
    const href = getActionHref(action, PhoneNumber, BookingLink, TextMessage);
    const {
        backgroundColor,
        boxColor,
        boxBorder,
        textColor,
        boxTextColor,
        backgroundStyle,
    } = getColorScheme(ColorScheme, GradientDirection);
    const surfaceColor = boxColor || backgroundColor;
    const surfaceTextColor = boxTextColor || textColor;
    const isUnavailable = href === '#' && !useNiceJobLead;
    const actionClassName = `group inline-flex min-h-14 items-center justify-center gap-2 rounded-full border border-white/20 px-4 text-sm font-bold shadow-xl ring-1 ring-black/10 transition duration-200 hover:-translate-y-0.5 hover:shadow-2xl focus:outline-none focus-visible:ring-4 focus-visible:ring-primary-300 ${surfaceColor} ${surfaceTextColor} ${
        boxBorder || ''
    } ${isUnavailable ? 'cursor-not-allowed' : ''} ${
        showLabel ? '' : 'h-14 w-14 px-0'
    }`;

    const actionContent = (
        <>
            <i className={`${icon} text-xl`} aria-hidden="true" />
            {showLabel && <span className="whitespace-nowrap">{label}</span>}
        </>
    );

    const handleClick = (event) => {
        if (is_edit_mode) {
            event.preventDefault();
            event.stopPropagation();
            return;
        }
        if (isUnavailable) event.preventDefault();
    };

    return (
        <div
            data-component="FloatingAction"
            className={`fixed bottom-5 z-[900] sm:bottom-7 ${
                isLeft ? 'left-5 sm:left-7' : 'right-5 sm:right-7'
            }`}
        >
            {useNiceJobLead ? (
                <button
                    type="button"
                    onClick={handleClick}
                    aria-label={label}
                    title={label}
                    className={`${actionClassName} nj-lead`}
                    style={backgroundStyle}
                >
                    {actionContent}
                </button>
            ) : (
                <a
                    href={href}
                    onClick={handleClick}
                    aria-label={label}
                    aria-disabled={isUnavailable}
                    title={label}
                    className={actionClassName}
                    style={backgroundStyle}
                    {...(action === 'book' && !is_edit_mode
                        ? buttonLinkTargetProps(BookingLinkOpenInNewTab)
                        : {})}
                >
                    {actionContent}
                </a>
            )}
        </div>
    );
}
